import React from 'react';
import { CheckCircle2, XCircle, HelpCircle, MinusCircle } from 'lucide-react';

export const CHECK_LABELS = {
  'device.removable_storage.connected': 'Removable storage connected',
  'device.removable_storage.mounted_usable': 'Removable storage mounted & usable',
  'device.removable_storage.policy': 'Removable-storage policy',
};

export const CHECK_ORDER = [
  'device.removable_storage.connected',
  'device.removable_storage.mounted_usable',
  'device.removable_storage.policy',
];

export const CHECK_STATUS_META = {
  pass: { label: 'Pass', dot: 'bg-emerald-500', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/25', Icon: CheckCircle2 },
  fail: { label: 'Fail', dot: 'bg-red-500', cls: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/25', Icon: XCircle },
  unknown: { label: 'Unknown', dot: 'bg-amber-500', cls: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/25', Icon: HelpCircle },
  not_applicable: { label: 'N/A', dot: 'bg-slate-400', cls: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-white/5 dark:text-white/50 dark:border-white/10', Icon: MinusCircle },
};

export const DEVICE_STATUS_META = {
  active: { label: 'Active', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/25' },
  pending: { label: 'Pending', cls: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-500/25' },
  inactive: { label: 'Inactive', cls: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/25' },
  retired: { label: 'Retired', cls: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-white/5 dark:text-white/50 dark:border-white/10' },
};

export const CODE_STATUS_META = {
  active: { label: 'Active', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/25' },
  expired: { label: 'Expired', cls: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-white/5 dark:text-white/50 dark:border-white/10' },
  exhausted: { label: 'Used up', cls: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/25' },
  revoked: { label: 'Revoked', cls: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/25' },
};

export const CheckStatusPill = ({ status, testId }) => {
  const m = CHECK_STATUS_META[status] || CHECK_STATUS_META.unknown;
  const Icon = m.Icon;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${m.cls}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
};

export const StatePill = ({ meta, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${meta.cls}`}>
    {meta.label}
  </span>
);

export const PostureBar = ({ summary }) => {
  const s = summary || {};
  const order = [
    ['pass', 'bg-emerald-500'],
    ['fail', 'bg-red-500'],
    ['unknown', 'bg-amber-500'],
    ['not_applicable', 'bg-slate-300 dark:bg-white/15'],
  ];
  const total = order.reduce((a, [k]) => a + (s[k] || 0), 0);
  if (!total) return <span className="text-[11px] text-black/40 dark:text-white/40">No data yet</span>;
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-1.5 w-24 overflow-hidden rounded-full bg-black/5 dark:bg-white/10">
        {order.map(([k, cls]) => (s[k] ? <div key={k} className={cls} style={{ width: `${(s[k] / total) * 100}%` }} /> : null))}
      </div>
      <span className="text-[11px] text-black/50 dark:text-white/50">
        {s.pass || 0}✓ · {s.fail || 0}✗ · {s.unknown || 0}? · {s.not_applicable || 0}–
      </span>
    </div>
  );
};

export function relativeTime(iso) {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr${h > 1 ? 's' : ''} ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d > 1 ? 's' : ''} ago`;
  return new Date(iso).toLocaleDateString();
}

export function observedText(observed) {
  if (!observed || typeof observed !== 'object') return '';
  return Object.entries(observed).map(([k, v]) => `${k}=${v}`).join(' · ');
}
